import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Gauge, Timer, CheckCircle2, AlertCircle,
  Info, Save, RotateCcw, Microscope, BarChart3, Zap,
  Thermometer, ArrowDown, ArrowRightLeft,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets,
  FlaskConical, Settings, Beaker, ArrowRight, Filter,
  Weight, Database
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

interface GACParams {
  flowRate: number;
  influentCOD: number;
  influentBOD: number;
  influentTSS: number;
  influentpH: number;
  temperature: number;
  og: number;
  ph: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
  vesselDiameter: number;
  bedDepth: number;
  freeboard: number;
  particleSize: number;   // mm
  bulkDensity: number;    // kg/m³
  numVessels: number;
  saturatorPressure: number;
  runTime: number;        // days
  compoundType: 'phenol' | 'chlorinated' | 'petroleum' | 'dye';
  targetCOD: number;
  backwashRate: number;
}

interface GACSize {
  vesselArea: number;
  vesselVolume: number;
  bedVolume: number;
  bedArea: number;
  ebct: number;
  ebctMin: number;
  surfaceLoadingRate: number;
  carbonMass: number;
  totalCarbonMass: number;
  adsorptionCapacity: number;
  breakthroughVolume: number;
  breakthroughDays: number;
  saturationPct: number;
  headLoss: number;
  headLossFouled: number;
  codRemoval: number;
  tssRemoval: number;
  reynoldsNumber: number;
  schmidtNumber: number;
  sherwoodNumber: number;
  massTransferCoeff: number;
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'Organic removal, moderate COD', icon: Milk,
    defaults: { flowRate: 250, influentCOD: 3200, influentBOD: 1850, influentTSS: 450, influentpH: 6.8, temperature: 25,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      vesselDiameter: 1.5, bedDepth: 2.0, freeboard: 0.5, particleSize: 1.5, bulkDensity: 500, numVessels: 2,
      saturatorPressure: 5, runTime: 20, compoundType: 'phenol' as const, targetCOD: 100, backwashRate: 20 } },
  { name: 'Brewery Effluent', description: 'High COD, phenol removal', icon: Factory,
    defaults: { flowRate: 350, influentCOD: 4100, influentBOD: 2200, influentTSS: 620, influentpH: 5.2, temperature: 20,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      vesselDiameter: 2.0, bedDepth: 2.5, freeboard: 0.5, particleSize: 2.0, bulkDensity: 500, numVessels: 3,
      saturatorPressure: 5, runTime: 14, compoundType: 'chlorinated' as const, targetCOD: 150, backwashRate: 20 } },
  { name: 'Fish Processing', description: 'Petroleum removal, oily effluent', icon: FishSymbol,
    defaults: { flowRate: 150, influentCOD: 2650, influentBOD: 1450, influentTSS: 520, influentpH: 6.9, temperature: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      vesselDiameter: 1.2, bedDepth: 1.8, freeboard: 0.5, particleSize: 1.2, bulkDensity: 500, numVessels: 2,
      saturatorPressure: 5, runTime: 25, compoundType: 'petroleum' as const, targetCOD: 80, backwashRate: 20 } },
];

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

export function calcGAC(p: GACParams): GACSize {
  const Q = p.flowRate / 24; // m³/h
  const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2);
  const mediaVolume = area * p.bedDepth;
  const emptyBedVolume = area * (p.bedDepth + p.freeboard);

  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (p.temperature - 20) / tempK);
  const rhoWater = 998 - 0.2 * (p.temperature - 20);

  const ebct = emptyBedVolume / Q; // h
  const ebctMin = ebct * 60; // min
  const velocity = Q / area; // m/h

  // Freundlich isotherm
  const freundlichK = p.compoundType === 'phenol' ? 21.0 : p.compoundType === 'chlorinated' ? 12.5 : p.compoundType === 'petroleum' ? 8.5 : 15.0;
  const freundlichN = p.compoundType === 'phenol' ? 0.25 : p.compoundType === 'chlorinated' ? 0.35 : p.compoundType === 'petroleum' ? 0.40 : 0.30;
  const equilibriumCapacity = freundlichK * Math.pow(p.targetCOD, freundlichN); // mg/g

  const carbonMass = mediaVolume * p.bulkDensity; // kg per vessel
  const totalCarbonMass = carbonMass * p.numVessels;
  const totalCapacity = totalCarbonMass * equilibriumCapacity; // g = mg adsorbate

  const dailyLoading = (p.flowRate * p.influentCOD) / 1000; // g/day COD
  const breakthroughDays = dailyLoading > 0 ? totalCapacity / dailyLoading : 999;
  const breakthroughVolume = breakthroughDays * p.flowRate; // m³
  const saturationPct = Math.min(100, (p.runTime / Math.max(1, breakthroughDays)) * 100);

  // Ergun equation
  const voidFraction = 0.40;
  const particleDiaM = p.particleSize / 1000;
  const u = velocity / 3600;
  const term1 = (150 * Math.pow(1 - voidFraction, 2) * mu * u) / (Math.pow(voidFraction, 3) * Math.pow(particleDiaM, 2));
  const term2 = (1.75 * (1 - voidFraction) * rhoWater * u * u) / (Math.pow(voidFraction, 3) * particleDiaM);
  const headLossPerM = (term1 + term2) / 9810;
  const totalHeadLoss = headLossPerM * p.bedDepth;
  const headLossFouled = totalHeadLoss * 1.5;

  // Mass transfer
  const diffusivity = 1e-9;
  const schmidtNumber = mu / (rhoWater * diffusivity);
  const reynoldsNumber = (rhoWater * velocity / 3600 * particleDiaM) / mu;
  const sherwoodNumber = 2 + 1.1 * Math.pow(reynoldsNumber, 0.6) * Math.pow(schmidtNumber, 0.33);
  const massTransferCoeff = sherwoodNumber * diffusivity / particleDiaM;

  // Removal efficiency
  const contactEfficiency = Math.min(1.0, ebctMin / 20);
  const codRemoval = Math.min(0.95, contactEfficiency * 0.85 * (1 - saturationPct / 200));
  const tssRemoval = Math.min(0.85, contactEfficiency * 0.7);

  let status: GACSize['status']; let statusDetail: string;
  const designNotes: string[] = [];

  if (ebct >= 0.5 && velocity <= 15 && breakthroughDays >= 30 && totalHeadLoss <= 3) {
    status = 'optimal';
    statusDetail = `Optimal. EBCT ${ebctMin.toFixed(0)} min, velocity ${velocity.toFixed(1)} m/h, breakthrough ${breakthroughDays.toFixed(0)} days.`;
  } else if (ebct >= 0.25 && velocity <= 25 && breakthroughDays >= 14 && totalHeadLoss <= 5) {
    status = 'acceptable';
    statusDetail = `Acceptable. EBCT ${ebctMin.toFixed(0)} min. Monitor breakthrough.`;
  } else if (velocity > 40 || totalHeadLoss > 8 || breakthroughDays < 7) {
    status = 'overloaded';
    statusDetail = `Overloaded. Velocity ${velocity.toFixed(1)} m/h too high. Reduce flow or increase diameter.`;
  } else if (mediaVolume < 1) {
    status = 'undersized';
    statusDetail = `Undersized. Media volume ${mediaVolume.toFixed(1)} m³. Increase bed depth.`;
  } else {
    status = 'oversized';
    statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  if (saturationPct > 80) designNotes.push('⚠️ Carbon near saturation. Plan replacement or regeneration.');
  if (totalHeadLoss > 5) designNotes.push('⚠️ High head loss. Backwash or replace media.');
  if (ebctMin < 5) designNotes.push('⚠️ EBCT below 5 min. Insufficient contact time for adsorption.');
  if (velocity > 25) designNotes.push('⚠️ Surface velocity too high. Risk of media fluidization.');
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    vesselArea: area, vesselVolume: emptyBedVolume, bedVolume: mediaVolume, bedArea: area,
    ebct, ebctMin, surfaceLoadingRate: velocity,
    carbonMass, totalCarbonMass,
    adsorptionCapacity: equilibriumCapacity,
    breakthroughVolume, breakthroughDays, saturationPct,
    headLoss: totalHeadLoss, headLossFouled,
    codRemoval, tssRemoval,
    reynoldsNumber, schmidtNumber, sherwoodNumber, massTransferCoeff,
    status, statusDetail, designNotes,
  };
}

function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : safeValue.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{fmt(safeValue, 1)} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

function FlowDiagram({ params, size }: { params: GACParams; size: GACSize }) {
  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — Influent → GAC Vessel → Effluent
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs><linearGradient id="waterGradG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" /><stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" /></linearGradient></defs>
        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">GRANULAR ACTIVATED CARBON — ADSORPTION PROCESS</text>

        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradG)" stroke="#f59e0b" strokeWidth="2" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/d</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">COD {fmt(params.influentCOD, 0)} mg/L</text>
        <text x="80" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">TSS {fmt(params.influentTSS, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">pH {fmt(params.influentpH, 1)}</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#f59e0b" />

        {/* GAC Vessel */}
        <rect x="180" y="40" width="250" height="110" rx="6" fill="url(#waterGradG)" stroke="#06b6d4" strokeWidth="2" />
        <text x="305" y="58" fill="#06b6d4" fontSize="10" textAnchor="middle" fontWeight="bold">GAC Vessel × {fmt(params.numVessels, 0)}</text>
        <text x="305" y="74" fill="#94a3b8" fontSize="8" textAnchor="middle">Ø {fmt(params.vesselDiameter, 1)} m × {fmt(params.bedDepth, 1)} m bed | {fmt(size.bedVolume, 1)} m³</text>
        <text x="305" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">EBCT {fmt(size.ebctMin, 0)} min | Vel {fmt(size.surfaceLoadingRate, 1)} m/h</text>
        <text x="305" y="102" fill="#f59e0b" fontSize="8" textAnchor="middle">Carbon {fmt(size.totalCarbonMass, 0)} kg | Capacity {fmt(size.adsorptionCapacity, 1)} mg/g</text>
        <text x="305" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">Head loss {fmt(size.headLoss, 2)} m | Saturation {fmt(size.saturationPct, 0)}%</text>

        {/* Media particles */}
        {Array.from({ length: 15 }).map((_, i) => (
          <circle key={i} cx={200 + (i % 5) * 45} cy={125 - (i % 3) * 8} r={2 + Math.random()} fill="#5c4033" opacity="0.4" />
        ))}

        <line x1="432" y1="95" x2="470" y2="95" stroke="#06b6d4" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="468,92 475,95 468,98" fill="#06b6d4" />

        <rect x="480" y="55" width="100" height="80" rx="6" fill="url(#waterGradG)" stroke="#10b981" strokeWidth="2" />
        <text x="530" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="530" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">COD {(params.influentCOD * (1 - size.codRemoval)).toFixed(0)} mg/L</text>
        <text x="530" y="100" fill="#10b981" fontSize="8" textAnchor="middle">Removal {(size.codRemoval * 100).toFixed(0)}%</text>
        <text x="530" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS {(params.influentTSS * (1 - size.tssRemoval)).toFixed(0)} mg/L</text>

        {/* Status panel */}
        <rect x="610" y="55" width="250" height="120" rx="6" fill="#0f172a" stroke={size.saturationPct > 80 ? '#ef4444' : size.saturationPct > 50 ? '#f59e0b' : '#10b981'} strokeWidth="1" />
        <text x="735" y="72" fill={size.saturationPct > 80 ? '#ef4444' : size.saturationPct > 50 ? '#f59e0b' : '#10b981'} fontSize="9" textAnchor="middle" fontWeight="bold">Adsorption Status</text>
        <text x="735" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Breakthrough: {fmt(size.breakthroughDays, 0)} days</text>
        <text x="735" y="100" fill="#94a3b8" fontSize="8" textAnchor="middle">Breakthrough vol: {fmt(size.breakthroughVolume, 0)} m³</text>
        <text x="735" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Saturation: {fmt(size.saturationPct, 0)}%</text>
        <text x="735" y="126" fill="#94a3b8" fontSize="8" textAnchor="middle">Re: {fmt(size.reynoldsNumber, 0)} | Sc: {fmt(size.schmidtNumber, 0)}</text>
        <text x="735" y="140" fill="#94a3b8" fontSize="8" textAnchor="middle">Sh: {fmt(size.sherwoodNumber, 1)} | kL: {fmt(size.massTransferCoeff, 4)} m/s</text>
        <text x="735" y="154" fill="#94a3b8" fontSize="8" textAnchor="middle">Compound: {params.compoundType}</text>

        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#f59e0b" fontSize="9">— Influent</text>
        <text x="110" y="359" fill="#06b6d4" fontSize="9">— GAC Adsorption</text>
        <text x="215" y="359" fill="#10b981" fontSize="9">— Effluent</text>
        <text x="300" y="359" fill="#94a3b8" fontSize="9">EBCT={fmt(size.ebctMin, 0)}min | HL={fmt(size.headLoss, 2)}m | Sat={fmt(size.saturationPct, 0)}%</text>
      </svg>
    </div>
  );
}

export default function GACSpecPage() {
  const [params, setParams] = useState<GACParams>(PROFILES[0].defaults);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'vessel' | 'adsorption'>('influent');
  const [profileIndex, setProfileIndex] = useState(0);

  const size = useMemo(() => calcGAC(params), [params]);
  const sampleLoader = useSampleLoader<GACParams>(setParams);

  const handleReset = () => { setParams(PROFILES[0].defaults); setProfileIndex(0); sampleLoader.clearSample(); toast.info('Parameters reset to defaults'); };
  const handleSave = () => { localStorage.setItem('gac-params-v2', JSON.stringify(params)); setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Configuration saved'); };

  useEffect(() => {
    const savedParams = localStorage.getItem('gac-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  const scienceSections = useMemo(() => {
    const Q = params.flowRate / 24;
    const area = Math.PI * Math.pow(params.vesselDiameter / 2, 2);
    const mediaVolume = area * params.bedDepth;
    const emptyBedVolume = area * (params.bedDepth + params.freeboard);
    const ebct = emptyBedVolume / Q;
    const ebctMin = ebct * 60;
    const velocity = Q / area;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.exp(-1.5 * (params.temperature - 20) / tempK);
    const rhoWater = 998 - 0.2 * (params.temperature - 20);
    const freundlichK = params.compoundType === 'phenol' ? 21.0 : params.compoundType === 'chlorinated' ? 12.5 : params.compoundType === 'petroleum' ? 8.5 : 15.0;
    const freundlichN = params.compoundType === 'phenol' ? 0.25 : params.compoundType === 'chlorinated' ? 0.35 : params.compoundType === 'petroleum' ? 0.40 : 0.30;
    const equilibriumCapacity = freundlichK * Math.pow(params.targetCOD, freundlichN);
    const carbonMass = mediaVolume * params.bulkDensity;
    const totalCarbonMass = carbonMass * params.numVessels;
    const totalCapacity = totalCarbonMass * equilibriumCapacity;
    const dailyLoading = (params.flowRate * params.influentCOD) / 1000;
    const breakthroughDays = dailyLoading > 0 ? totalCapacity / dailyLoading : 999;
    const saturationPct = Math.min(100, (params.runTime / Math.max(1, breakthroughDays)) * 100);
    const voidFraction = 0.40;
    const particleDiaM = params.particleSize / 1000;
    const u = velocity / 3600;
    const term1 = (150 * Math.pow(1 - voidFraction, 2) * mu * u) / (Math.pow(voidFraction, 3) * Math.pow(particleDiaM, 2));
    const term2 = (1.75 * (1 - voidFraction) * rhoWater * u * u) / (Math.pow(voidFraction, 3) * particleDiaM);
    const headLossPerM = (term1 + term2) / 9810;
    const totalHeadLoss = headLossPerM * params.bedDepth;
    const diffusivity = 1e-9;
    const schmidtNumber = mu / (rhoWater * diffusivity);
    const reynoldsNumber = (rhoWater * velocity / 3600 * particleDiaM) / mu;
    const sherwoodNumber = 2 + 1.1 * Math.pow(reynoldsNumber, 0.6) * Math.pow(schmidtNumber, 0.33);
    const massTransferCoeff = sherwoodNumber * diffusivity / particleDiaM;
    const contactEfficiency = Math.min(1.0, ebctMin / 20);
    const codRemoval = Math.min(0.95, contactEfficiency * 0.85 * (1 - saturationPct / 200));
    const tssRemoval = Math.min(0.85, contactEfficiency * 0.7);
    return [
      {
        title: 'Adsorption Physics — Freundlich Isotherm & EBCT',
        icon: Beaker,
        equations: [
          { label: 'Flow rate (hourly)', formula: 'Q = flowRate / 24', substitution: `Q = ${sharedFmt(params.flowRate, 0)} / 24 = ${sharedFmt(Q, 1)} m³/h`, result: sharedFmt(Q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Vessel area', formula: 'A = π × (D/2)²', substitution: `A = π × (${params.vesselDiameter}/2)² = ${sharedFmt(area, 2)} m²`, result: sharedFmt(area, 2), status: 'pass', unit: 'm²' },
          { label: 'Media volume', formula: 'V_media = A × bedDepth', substitution: `V_media = ${sharedFmt(area, 2)} × ${params.bedDepth} = ${sharedFmt(mediaVolume, 1)} m³`, result: sharedFmt(mediaVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Empty bed volume', formula: 'V_eb = A × (bedDepth + freeboard)', substitution: `V_eb = ${sharedFmt(area, 2)} × (${params.bedDepth} + ${params.freeboard}) = ${sharedFmt(emptyBedVolume, 1)} m³`, result: sharedFmt(emptyBedVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'EBCT', formula: 'EBCT = V_eb / Q', substitution: `EBCT = ${sharedFmt(emptyBedVolume, 1)} / ${sharedFmt(Q, 1)} = ${sharedFmt(ebct, 2)} h`, result: sharedFmt(ebct, 2), status: ebct >= 0.25 ? 'pass' : 'warn', unit: 'h' },
          { label: 'EBCT (minutes)', formula: 'EBCT_min = EBCT × 60', substitution: `EBCT_min = ${sharedFmt(ebct, 2)} × 60 = ${sharedFmt(ebctMin, 0)} min`, result: sharedFmt(ebctMin, 0), status: ebctMin >= 5 ? 'pass' : 'warn', unit: 'min' },
          { label: 'Freundlich K', formula: 'K = f(compoundType)', substitution: `K = ${freundlichK} (${params.compoundType})`, result: sharedFmt(freundlichK, 1), status: 'pass', unit: '' },
          { label: 'Freundlich n', formula: 'n = f(compoundType)', substitution: `n = ${freundlichN} (${params.compoundType})`, result: sharedFmt(freundlichN, 2), status: 'pass', unit: '' },
          { label: 'Equilibrium capacity', formula: 'q_e = K × C_e^n', substitution: `q_e = ${freundlichK} × ${params.targetCOD}^${freundlichN} = ${sharedFmt(equilibriumCapacity, 1)} mg/g`, result: sharedFmt(equilibriumCapacity, 1), status: 'pass', unit: 'mg/g' },
          { label: 'Carbon mass per vessel', formula: 'm_c = V_media × ρ_bulk', substitution: `m_c = ${sharedFmt(mediaVolume, 1)} × ${params.bulkDensity} = ${sharedFmt(carbonMass, 0)} kg`, result: sharedFmt(carbonMass, 0), status: 'pass', unit: 'kg' },
          { label: 'Total carbon mass', formula: 'm_total = m_c × N_vessels', substitution: `m_total = ${sharedFmt(carbonMass, 0)} × ${params.numVessels} = ${sharedFmt(totalCarbonMass, 0)} kg`, result: sharedFmt(totalCarbonMass, 0), status: 'pass', unit: 'kg' },
          { label: 'Total adsorption capacity', formula: 'Q_total = m_total × q_e', substitution: `Q_total = ${sharedFmt(totalCarbonMass, 0)} × ${sharedFmt(equilibriumCapacity, 1)} = ${sharedFmt(totalCapacity, 0)} g`, result: sharedFmt(totalCapacity, 0), status: 'pass', unit: 'g' },
        ],
      },
      {
        title: 'Hydraulics — Ergun Equation & Head Loss',
        icon: Droplets,
        equations: [
          { label: 'Superficial velocity', formula: 'v = Q / A', substitution: `v = ${sharedFmt(Q, 1)} / ${sharedFmt(area, 2)} = ${sharedFmt(velocity, 1)} m/h`, result: sharedFmt(velocity, 1), status: velocity <= 25 ? 'pass' : 'warn', unit: 'm/h' },
          { label: 'Void fraction', formula: 'ε = 0.40 (assumed)', substitution: `ε = 0.40`, result: '0.40', status: 'pass', unit: '' },
          { label: 'Particle diameter', formula: 'd_p = particleSize / 1000', substitution: `d_p = ${params.particleSize} / 1000 = ${sharedFmt(particleDiaM, 4)} m`, result: sharedFmt(particleDiaM, 4), status: 'pass', unit: 'm' },
          { label: 'Interstitial velocity', formula: 'u = v / 3600', substitution: `u = ${sharedFmt(velocity, 1)} / 3600 = ${sharedFmt(u, 4)} m/s`, result: sharedFmt(u, 4), status: 'pass', unit: 'm/s' },
          { label: 'Ergun term 1 (viscous)', formula: 'term1 = 150(1−ε)²μu / (ε³d_p²)', substitution: `term1 = ${sharedFmt(term1, 1)} Pa/m`, result: sharedFmt(term1, 1), status: 'pass', unit: 'Pa/m' },
          { label: 'Ergun term 2 (inertial)', formula: 'term2 = 1.75(1−ε)ρu² / (ε³d_p)', substitution: `term2 = ${sharedFmt(term2, 1)} Pa/m`, result: sharedFmt(term2, 1), status: 'pass', unit: 'Pa/m' },
          { label: 'Head loss per metre', formula: 'ΔP/L = (term1 + term2) / 9810', substitution: `ΔP/L = (${sharedFmt(term1, 1)} + ${sharedFmt(term2, 1)}) / 9810 = ${sharedFmt(headLossPerM, 3)} m/m`, result: sharedFmt(headLossPerM, 3), status: 'pass', unit: 'm/m' },
          { label: 'Total head loss', formula: 'h_L = ΔP/L × bedDepth', substitution: `h_L = ${sharedFmt(headLossPerM, 3)} × ${params.bedDepth} = ${sharedFmt(totalHeadLoss, 2)} m`, result: sharedFmt(totalHeadLoss, 2), status: totalHeadLoss <= 5 ? 'pass' : 'warn', unit: 'm' },
        ],
      },
      {
        title: 'Mass Transfer — Reynolds, Schmidt, Sherwood',
        icon: Filter,
        equations: [
          { label: 'Diffusivity', formula: 'D = 1×10⁻⁹ m²/s (assumed)', substitution: `D = 1×10⁻⁹ m²/s`, result: '1e-9', status: 'pass', unit: 'm²/s' },
          { label: 'Schmidt number', formula: 'Sc = μ / (ρ_w × D)', substitution: `Sc = ${sharedFmt(mu, 5)} / (${sharedFmt(rhoWater, 0)} × 1e-9) = ${sharedFmt(schmidtNumber, 0)}`, result: sharedFmt(schmidtNumber, 0), status: 'pass', unit: '' },
          { label: 'Reynolds number', formula: 'Re = ρ_w × u × d_p / μ', substitution: `Re = ${sharedFmt(rhoWater, 0)} × ${sharedFmt(u, 4)} × ${sharedFmt(particleDiaM, 4)} / ${sharedFmt(mu, 5)} = ${sharedFmt(reynoldsNumber, 0)}`, result: sharedFmt(reynoldsNumber, 0), status: reynoldsNumber < 10 ? 'pass' : 'warn', unit: '' },
          { label: 'Sherwood number', formula: 'Sh = 2 + 1.1×Re^0.6×Sc^0.33', substitution: `Sh = 2 + 1.1×${sharedFmt(reynoldsNumber, 0)}^0.6×${sharedFmt(schmidtNumber, 0)}^0.33 = ${sharedFmt(sherwoodNumber, 1)}`, result: sharedFmt(sherwoodNumber, 1), status: 'pass', unit: '' },
          { label: 'Mass transfer coefficient', formula: 'k_L = Sh × D / d_p', substitution: `k_L = ${sharedFmt(sherwoodNumber, 1)} × 1e-9 / ${sharedFmt(particleDiaM, 4)} = ${sharedFmt(massTransferCoeff, 6)} m/s`, result: sharedFmt(massTransferCoeff, 6), status: 'pass', unit: 'm/s' },
        ],
      },
      {
        title: 'Removal Efficiency & Breakthrough',
        icon: BarChart3,
        equations: [
          { label: 'Contact efficiency', formula: 'η_c = min(1, EBCT_min / 20)', substitution: `η_c = min(1, ${sharedFmt(ebctMin, 0)} / 20) = ${sharedFmt(contactEfficiency, 2)}`, result: sharedFmt(contactEfficiency, 2), status: contactEfficiency >= 0.5 ? 'pass' : 'warn', unit: '' },
          { label: 'Saturation', formula: 'S = runTime / breakthrough × 100', substitution: `S = ${params.runTime} / ${sharedFmt(breakthroughDays, 0)} × 100 = ${sharedFmt(saturationPct, 0)}%`, result: sharedFmt(saturationPct, 0), status: 'pass', unit: '%' },
          { label: 'COD removal', formula: 'η_COD = min(0.95, η_c × 0.85 × (1 − S/200))', substitution: `η_COD = ${sharedFmt(codRemoval * 100, 0)}%`, result: sharedFmt(codRemoval * 100, 0), status: codRemoval >= 0.5 ? 'pass' : 'warn', unit: '%' },
          { label: 'TSS removal', formula: 'η_TSS = min(0.85, η_c × 0.7)', substitution: `η_TSS = ${sharedFmt(tssRemoval * 100, 0)}%`, result: sharedFmt(tssRemoval * 100, 0), status: tssRemoval >= 0.3 ? 'pass' : 'warn', unit: '%' },
          { label: 'Daily COD loading', formula: 'L = flowRate × COD / 1000', substitution: `L = ${sharedFmt(params.flowRate, 0)} × ${params.influentCOD} / 1000 = ${sharedFmt(dailyLoading, 1)} g/day`, result: sharedFmt(dailyLoading, 1), status: 'pass', unit: 'g/day' },
          { label: 'Breakthrough days', formula: 't_b = Q_total / L', substitution: `t_b = ${sharedFmt(totalCapacity, 0)} / ${sharedFmt(dailyLoading, 1)} = ${sharedFmt(breakthroughDays, 0)} days`, result: sharedFmt(breakthroughDays, 0), status: breakthroughDays >= 14 ? 'pass' : 'warn', unit: 'days' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Filter}
        iconColor="text-emerald-400"
        title="GAC Filter Specification"
        subtitle="Granular activated carbon with Freundlich isotherm, Ergun head loss, and mass transfer physics"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=gac')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      <div className={`rounded-xl border p-4 ${sc.bg} ${sc.border}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.bg}`}><StatusIcon className={`w-5 h-5 ${sc.text}`} /></div>
          <div><p className={`text-sm font-bold ${sc.text}`}>{size.status.toUpperCase()}</p><p className={`text-xs ${sc.text} opacity-80`}>{size.statusDetail}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROFILES.map((profile, i) => (
          <PC key={i} profile={profile} isActive={profileIndex === i} onClick={() => { setParams(profile.defaults); setProfileIndex(i); toast.info(`Loaded ${profile.name} profile`); }} />
        ))}
      </div>

      <SampleSelector loader={sampleLoader} />

      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-emerald-400" />Process Flow</h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"><Settings className="w-3 h-3" />{showFlow ? 'Collapse' : 'Expand'}</button>
          </div>
          <AnimatePresence>{showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}</AnimatePresence>
        </div>

        <div className="xl:col-span-5 space-y-4">
          <div className="p-4 rb-card">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" />Performance Gauges</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <G value={size.ebctMin} max={30} label="EBCT" unit="min" color="#10b981" />
              <G value={size.surfaceLoadingRate} max={25} label="Velocity" unit="m/h" color="#06b6d4" />
              <G value={size.breakthroughDays} max={60} label="Breakthrough" unit="days" color="#f59e0b" />
              <G value={size.saturationPct} max={100} label="Saturation" unit="%" color="#ef4444" />
              <G value={size.headLoss} max={6} label="Head Loss" unit="m" color="#a855f7" />
              <G value={size.codRemoval * 100} max={100} label="COD Removal" unit="%" color="#f97316" />
              <G value={size.tssRemoval * 100} max={100} label="TSS Removal" unit="%" color="#22c55e" />
              <G value={size.totalCarbonMass} max={500} label="Carbon Mass" unit="kg" color="#8b5cf6" />
            </div>
          </div>

          <div className="overflow-hidden rb-card">
            <div className="flex border-b border-slate-800">
              {(['influent', 'vessel', 'adsorption'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${activeTab === tab ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300'}`}>
                  {tab === 'influent' ? 'Influent' : tab === 'vessel' ? 'Vessel Design' : 'Adsorption'}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'influent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4" onChange={(v: number) => setParams({ ...params, flowRate: v })} />
                  <S label="Influent COD" value={params.influentCOD} min={200} max={8000} step={100} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentCOD: v })} />
                  <S label="Influent BOD" value={params.influentBOD} min={100} max={5000} step={50} unit="mg/L" icon={FlaskConical} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentBOD: v })} />
                  <S label="Influent TSS" value={params.influentTSS} min={50} max={2000} step={50} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentTSS: v })} />
                  <S label="Influent pH" value={params.influentpH} min={4} max={9} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentpH: v })} />
                  <S label="Temperature" value={params.temperature} min={5} max={35} step={1} unit="°C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temperature: v })} />
                  <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="Influent pH (sample)" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} />
                </div>
              )}
              {activeTab === 'vessel' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Vessel Diameter" value={params.vesselDiameter} min={0.5} max={3} step={0.1} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, vesselDiameter: v })} />
                  <S label="Bed Depth" value={params.bedDepth} min={0.5} max={4} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, bedDepth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="Particle Size" value={params.particleSize} min={0.5} max={4} step={0.1} unit="mm" icon={Droplets} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, particleSize: v })} />
                  <S label="Bulk Density" value={params.bulkDensity} min={300} max={700} step={50} unit="kg/m³" icon={Weight} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, bulkDensity: v })} />
                  <S label="Num Vessels" value={params.numVessels} min={1} max={6} step={1} unit="" icon={Gauge} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, numVessels: v })} />
                </div>
              )}
              {activeTab === 'adsorption' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Compound Type</p>
                    <div className="flex gap-2 flex-wrap">
                      {(['phenol', 'chlorinated', 'petroleum', 'dye'] as const).map((t) => (
                        <button key={t} onClick={() => setParams({ ...params, compoundType: t })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${params.compoundType === t ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <S label="Target COD" value={params.targetCOD} min={20} max={500} step={10} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, targetCOD: v })} />
                  <S label="Run Time" value={params.runTime} min={1} max={60} step={1} unit="days" icon={Timer} color="#f59e0b" onChange={(v: number) => setParams({ ...params, runTime: v })} />
                  <S label="Backwash Rate" value={params.backwashRate} min={10} max={40} step={1} unit="m/h" icon={ArrowRightLeft} color="#0288D1" onChange={(v: number) => setParams({ ...params, backwashRate: v })} />
                </div>
              )}
            </div>
          </div>

          <div className="p-4 space-y-4 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" />Results & Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Effluent Quality</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">COD</span><span className="text-white font-bold">{fmt(params.influentCOD, 0)} → {(params.influentCOD * (1 - size.codRemoval)).toFixed(0)} mg/L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Removal</span><span className="text-emerald-400 font-bold">{(size.codRemoval * 100).toFixed(0)}%</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">TSS</span><span className="text-white font-bold">{fmt(params.influentTSS, 0)} → {(params.influentTSS * (1 - size.tssRemoval)).toFixed(0)} mg/L</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Adsorption Status</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Breakthrough</span><span className="text-sky-400 font-bold">{fmt(size.breakthroughDays, 0)} days</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Saturation</span><span className={`${size.saturationPct > 80 ? 'text-rose-400' : size.saturationPct > 50 ? 'text-amber-400' : 'text-emerald-400'} font-bold`}>{fmt(size.saturationPct, 0)} %</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Capacity</span><span className="text-sky-400 font-bold">{fmt(size.adsorptionCapacity, 1)} mg/g</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Carbon mass</span><span className="text-sky-400 font-bold">{fmt(size.totalCarbonMass, 0)} kg</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Hydraulics & Physics</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Head loss</span><span className="text-sky-400 font-bold">{fmt(size.headLoss, 2)} m</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Head loss (fouled)</span><span className="text-sky-400 font-bold">{fmt(size.headLossFouled, 2)} m</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">EBCT</span><span className="text-sky-400 font-bold">{fmt(size.ebctMin, 0)} min</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Reynolds</span><span className="text-sky-400 font-bold">{fmt(size.reynoldsNumber, 0)}</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes & Warnings</h3>
            <div className="space-y-2">
              {size.designNotes.map((note, i) => (
                <div key={i} className={`text-xs p-2 rounded-lg ${note.startsWith('⚠️') ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400' : note.startsWith('ℹ️') ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400' : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'}`}>{note}</div>
              ))}
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
